/**
 * Chart theme resolution.
 *
 * The palette is chosen from the LIVE `--os-window-body` token, not from a
 * hardcoded mode and not from `prefers-color-scheme`. ArcLog is dark-first
 * (#0e1017) but `theme-windows-classic` paints the window body a light
 * rgba(212,208,200,.94) — a chart that reads the OS preference instead of the
 * actual surface it is sitting on gets that case exactly backwards.
 *
 * Text colours come from the OS text tokens. Series colour is NEVER used for
 * text: identity is carried by the coloured mark beside the label.
 */

import { flatten, modeForSurface, withAlpha } from "./charts-color.mjs";
import {
  OTHER_COLOR,
  STATUS_COLORS,
  categoricalPalette,
  lightModeReliefRequired,
} from "./charts-palette.mjs";
import type { ChartMode, ChartTheme, ChartThemePreference } from "./types";

const DARK_SURFACE = "#0e1017";
const LIGHT_SURFACE = "#d4d0c8";

function token(el: Element | null, name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  try {
    const target = el && el.isConnected ? el : document.documentElement;
    const v = getComputedStyle(target).getPropertyValue(name).trim();
    return v || fallback;
  } catch {
    return fallback;
  }
}

/**
 * The opaque colour a chart is actually drawn on top of.
 *
 * Read from the chart's own element so a themed ancestor wins, then flattened
 * over `--os-bg`: the window body is often translucent, and the luminance of a
 * translucent colour on its own is meaningless.
 */
export function resolveSurface(el: Element | null): string {
  const body = token(el, "--os-window-body", DARK_SURFACE);
  const bg = token(el, "--os-bg", DARK_SURFACE);
  return flatten(body, flatten(bg, DARK_SURFACE));
}

/** Palette mode for a chart, honouring an explicit override. */
export function resolveMode(el: Element | null, preference: ChartThemePreference): ChartMode {
  if (preference === "light" || preference === "dark") return preference;
  return modeForSurface(resolveSurface(el));
}

/**
 * Build the full theme. `preference` of "light"/"dark" pins the palette but the
 * surface is still read live, so a pinned-dark chart on a light theme is drawn
 * on the real (light) background rather than an invented one.
 */
export function resolveChartTheme(
  el: Element | null,
  preference: ChartThemePreference = "auto"
): ChartTheme {
  const surface = resolveSurface(el);
  const mode = resolveMode(el, preference);
  const fallbackSurface = mode === "light" ? LIGHT_SURFACE : DARK_SURFACE;

  // Text tokens flattened onto the surface — the OS ships them translucent.
  const text = flatten(token(el, "--os-text", mode === "light" ? "#1c1c1e" : "#f2f3f5"), surface);
  const textDim = flatten(
    token(el, "--os-text-dim", mode === "light" ? "rgba(0,0,0,.6)" : "rgba(255,255,255,.55)"),
    surface
  );
  const textFaint = flatten(
    token(el, "--os-text-faint", mode === "light" ? "rgba(0,0,0,.38)" : "rgba(255,255,255,.32)"),
    surface
  );

  const ink = mode === "light" ? "#000000" : "#ffffff";

  return {
    mode,
    surface: surface || fallbackSurface,
    // Grid and axes are recessive: they orient the eye, they are not data.
    grid: withAlpha(ink, mode === "light" ? 0.1 : 0.08),
    axis: withAlpha(ink, mode === "light" ? 0.2 : 0.16),
    text,
    textDim,
    textFaint,
    accent: token(el, "--os-accent", "#0a84ff"),
    tooltipBg: mode === "light" ? "rgba(255,255,255,.97)" : "rgba(24,26,34,.96)",
    tooltipBorder: withAlpha(ink, mode === "light" ? 0.18 : 0.14),
    crosshair: withAlpha(ink, mode === "light" ? 0.32 : 0.28),
    palette: categoricalPalette(mode),
    other: OTHER_COLOR[mode],
    status: { ...STATUS_COLORS[mode] },
    fontUi: token(el, "--os-font-ui", "system-ui, -apple-system, sans-serif"),
    fontMono: token(el, "--os-font-mono", "ui-monospace, Menlo, monospace"),
    // Text on a filled series hue (e.g. `endTag`), not `surface`: in dark mode
    // surface is near-black on a mid-dark palette hue and would be unreadable.
    onAccent: token(el, "--os-text-on-accent", "#fff"),
    /**
     * The light palette carries a non-dismissable contrast WARN (6 of 8 hues
     * below 3:1 against #d4d0c8). The standard's remedy is visible labels or a
     * table view, so the chart forces both rather than trusting the app author
     * to remember.
     */
    reliefRequired: lightModeReliefRequired(mode),
  };
}
